import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Notification, NotificationType } from 'clipsal-cortex-types/src/api/api-notifications';

import { NotificationGrouping, useGetNotifications } from './notificationsApi';

/**
 * Returns the translated display title for a notification type.
 *
 * @param type - The notification type
 * @returns The translated title string
 */
export function useGetNotificationTitle(type: NotificationType) {
  const { t } = useTranslation();
  return t(`Notifications.types.${type}.title`);
}

export const NOTIFICATIONS_PAGE_SIZE = 10;
// While a search term is active, fetch in much larger pages so the title filter runs over (close to) the
// full notification set rather than just the first page.
export const NOTIFICATIONS_SEARCH_PAGE_SIZE = 100;

/**
 * Returns a predicate matching notifications whose displayed title contains the search term
 * (case-insensitive). The displayed title mirrors `NotificationListItem` — the notification's own
 * title, falling back to the translated title for its type.
 *
 * The filtering happens client-side (in addition to the `search` query param sent to the API) so
 * searching works regardless of backend support for that param.
 */
export function useNotificationSearchMatcher(searchTerm: string) {
  const { t } = useTranslation();
  const term = searchTerm.trim().toLowerCase();
  return useCallback(
    (notification: Notification) => {
      if (!term) return true;
      const displayTitle = notification.title || t(`Notifications.types.${notification.notification_type}.title`);
      return displayTitle.toLowerCase().includes(term);
    },
    [term, t]
  );
}

type NotificationQueries = ReturnType<typeof useGetNotifications>['data'];

/** Returns the queries which feed the currently selected grouping's list sections. */
function getActiveQueries(queryData: NotificationQueries, groupBy: NotificationGrouping) {
  switch (groupBy) {
    case 'CHRONOLOGICAL':
      return [queryData.chronological];
    case 'UNREAD':
      return [queryData.unread, queryData.read];
    case 'PRIORITY':
      return [queryData.alarm, queryData.warning, queryData.info];
    default:
      return [];
  }
}

/** Returns the IDs of every notification currently rendered for the selected grouping (used for "Select All"). */
export function getAllVisibleNotificationIds(
  queryData: NotificationQueries,
  groupBy: NotificationGrouping,
  matchesSearch: (notification: Notification) => boolean = () => true
): string[] {
  return getActiveQueries(queryData, groupBy).flatMap(
    (query) => query?.data?.results?.filter(matchesSearch).map((n) => n.notification_id) ?? []
  );
}

/** Whether any section in the selected grouping has more notifications on the server than currently loaded. */
export function getHasMoreNotifications(queryData: NotificationQueries, groupBy: NotificationGrouping): boolean {
  return getActiveQueries(queryData, groupBy).some((query) => {
    const data = query?.data;
    return !!data && data.results.length < data.count;
  });
}

/** The largest total notification count across the active grouping's sections (the full set size on the server). */
function getMaxNotificationCount(queryData: NotificationQueries, groupBy: NotificationGrouping): number {
  return getActiveQueries(queryData, groupBy).reduce((max, query) => Math.max(max, query?.data?.count ?? 0), 0);
}

/**
 * Fetches notifications for the selected grouping, growing the query `limit` by a page each time `loadMore`
 * is called (via the "Load more notifications" card), in place of explicit pagination. Growing the limit
 * (rather than accumulating offset pages client-side) keeps the loaded list consistent with the 15s polling
 * refetches and RTK Query tag invalidation.
 *
 * The limit resets whenever the selected grouping or the search term changes.
 *
 * @param groupBy - The currently selected notification grouping
 * @param isArchived - Whether to fetch archived notifications
 * @param search - Only fetch notifications whose title matches this search term
 * @returns The notification queries, the current query limit, whether more notifications are available, and
 *          the `loadMore` callback
 */
export function useLoadMoreNotifications(groupBy: NotificationGrouping, isArchived = false, search = '') {
  // Searching fetches in much larger pages so the client-side title filter covers the whole set
  const pageSize = search ? NOTIFICATIONS_SEARCH_PAGE_SIZE : NOTIFICATIONS_PAGE_SIZE;
  const [limit, setLimit] = useState(pageSize);

  // Reset the limit when grouping/search changes without setting state during render.
  const queryKey = `${groupBy}:${search}`;
  const prevQueryKeyRef = useRef(queryKey);
  const isNewQueryKey = prevQueryKeyRef.current !== queryKey;
  const effectiveLimit = isNewQueryKey ? pageSize : limit;

  useEffect(() => {
    if (!isNewQueryKey) return;
    prevQueryKeyRef.current = queryKey;
    setLimit(pageSize);
  }, [isNewQueryKey, queryKey, pageSize]);

  const { data, isFetching } = useGetNotifications(groupBy, {
    limit: effectiveLimit,
    offset: 0,
    archived: isArchived,
    search: search || undefined,
  });
  const hasMore = getHasMoreNotifications(data, groupBy);
  const totalCount = getMaxNotificationCount(data, groupBy);

  // While a search term is active, expand the limit to cover the entire notification set as soon as we learn
  // its size, so the client-side title filter matches against every notification — not just the first page.
  // The backend may ignore the `search` param, in which case the matching notifications can sit anywhere in
  // the full list; loading them all up front (in a single jump, not page-by-page) is what makes search reliable.
  useEffect(() => {
    if (search && !isFetching && totalCount > limit) setLimit(totalCount);
  }, [search, isFetching, totalCount, limit]);

  function loadMore() {
    if (hasMore && !isFetching) setLimit((prevLimit) => prevLimit + pageSize);
  }

  return { data, limit, hasMore, isFetching, loadMore };
}
